import { useState, useEffect, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FolderOpen, Upload, StickyNote, Download, Trash2, Loader2, FileText, MessageSquareText, GraduationCap, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const ICON = { upload: FileText, note: StickyNote, transcript: MessageSquareText, interview_report: GraduationCap, template: ClipboardList };

export default function Evidence() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", key_question: "", note: "", file: null });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/evidence").then((r) => { setData(r.data); setTab((t) => t || r.data.key_questions[0]); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (form.file) {
        const fd = new FormData();
        fd.append("title", form.title || form.file.name); fd.append("key_question", form.key_question || tab);
        fd.append("note", form.note); fd.append("file", form.file);
        await api.post("/evidence/upload", fd);
      } else {
        await api.post("/evidence", { title: form.title, key_question: form.key_question || tab, note: form.note, source_type: "note" });
      }
      toast.success("Added to your evidence folder");
      setOpen(false); setForm({ title: "", key_question: "", note: "", file: null }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const download = async (e) => {
    try {
      const res = await api.get(`/evidence/${e.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = e.file.filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed"); }
  };

  const remove = async (e) => { await api.delete(`/evidence/${e.id}`); toast.success("Removed"); load(); };

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  const items = data.items.filter((i) => i.key_question === tab);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="evidence-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <span className="overline text-accent">Evidence folder</span>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Inspection evidence by key question</h1>
          <p className="text-muted-foreground mt-2">File transcripts, reports, audits and templates under each {data.regulator} area so nothing is scrambled for on the day.</p>
        </div>
        <Button onClick={() => { setForm({ title: "", key_question: tab, note: "", file: null }); setOpen(true); }} data-testid="evidence-add-button">
          <Upload className="w-4 h-4 mr-1.5" /> Add evidence
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap mb-6" data-testid="evidence-tabs">
        {data.key_questions.map((k) => {
          const n = data.items.filter((i) => i.key_question === k).length;
          return (
            <button key={k} onClick={() => setTab(k)} data-testid={`evidence-tab-${k.replace(/\W+/g, "-").toLowerCase()}`}
              className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${tab === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
              {k} <span className={`ml-1 text-xs ${tab === k ? "opacity-80" : "text-muted-foreground"}`}>{n}</span>
            </button>
          );
        })}
      </div>

      {items.length ? (
        <ul className="space-y-3" data-testid="evidence-list">
          {items.map((e) => {
            const Icon = ICON[e.source_type] || FileText;
            return (
              <li key={e.id} className="bg-card border border-border rounded-2xl p-4 flex items-start gap-3 shadow-sm" data-testid={`evidence-item-${e.id}`}>
                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium truncate">{e.title}</p>
                    <Badge variant="secondary" className="text-[10px] capitalize">{e.source_type.replace("_", " ")}</Badge>
                    <span className="text-xs text-muted-foreground ml-auto">{new Date(e.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                  </div>
                  {e.note && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{e.note}</p>}
                  {e.file && <p className="text-xs text-muted-foreground mt-1">{e.file.filename} · {Math.max(1, Math.round(e.file.size / 1024))} KB</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  {e.file && <Button size="sm" variant="outline" onClick={() => download(e)} data-testid={`evidence-download-${e.id}`}><Download className="w-4 h-4" /></Button>}
                  <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => remove(e)} data-testid={`evidence-delete-${e.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-2xl" data-testid="evidence-empty">
          <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>Nothing filed under <strong>{tab}</strong> yet.</p>
          <p className="text-xs mt-1">Use "Save to evidence" on mock interview reports and AI transcripts, or add a note or upload here.</p>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="evidence-dialog">
          <DialogHeader><DialogTitle className="font-display">Add evidence</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Key question</Label>
              <Select value={form.key_question || tab} onValueChange={(v) => setForm({ ...form, key_question: v })}>
                <SelectTrigger className="mt-1.5" data-testid="evidence-kq-select"><SelectValue /></SelectTrigger>
                <SelectContent>{data.key_questions.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. March medicines audit" className="mt-1.5" data-testid="evidence-title-input" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} className="mt-1.5" data-testid="evidence-note-input" placeholder="What does this evidence show?" />
            </div>
            <div>
              <Label>Attach a file (optional, up to 10 MB)</Label>
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.png,.jpg,.jpeg" className="hidden"
                onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="evidence-file-input" />
              <div className="mt-1.5 flex items-center gap-3">
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="evidence-choose-file"><Upload className="w-4 h-4 mr-1.5" /> Choose file</Button>
                <span className="text-xs text-muted-foreground truncate">{form.file ? form.file.name : "No file selected"}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || (!form.file && form.title.trim().length < 2)} data-testid="evidence-save-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
